import React, { useState, useEffect } from 'react';
import clientGenerator from './clientGenerator';
import templates from './blogTemplates.json';
import { Button } from '../../ui/button';
import { cn } from '../../../lib/utils';

// Simple, self-contained blog post selector + generator UI.
// Usage: <BlogPostSelector onApply={(variant) => { ... }} />
export default function BlogPostSelector({ onApply, onGenerated, llmStatus, selectedBlog = null }) {
  const [title, setTitle] = useState('');
  const [excerpt, setExcerpt] = useState('');
  const [url, setUrl] = useState('');
  const [tags, setTags] = useState('');
  const [templateId, setTemplateId] = useState(Object.keys(templates)[0]);
  const [tone, setTone] = useState('professional');
  const [length, setLength] = useState('short');
  const [loading, setLoading] = useState(false);
  const [variants, setVariants] = useState([]);
  const [error, setError] = useState('');

  async function handleGenerate() {
    setError('');
    
    // Use the selectedBlog data if available, otherwise use form data
    let blog;
    if (selectedBlog && selectedBlog.id) {
      // Use the complete blog data from Firebase, ensuring we have all content
      blog = {
        id: selectedBlog.id,
        title: selectedBlog.title || title,
        excerpt: selectedBlog.excerpt || excerpt,
        content: selectedBlog.content || '', // Full article content
        body: selectedBlog.body || '', // Alternative content field
        summary: selectedBlog.summary || '',
        metaDescription: selectedBlog.metaDescription || '',
        rawContent: selectedBlog.rawContent || '',
        url: selectedBlog.url || url,
        tags: selectedBlog.tags || (tags.split(',').map(s => s.trim()).filter(Boolean)),
        categories: selectedBlog.categories || [],
        slug: selectedBlog.slug || ''
      };
      console.log('[BlogPostSelector] Using selectedBlog data with content length:', blog.content?.length || 0);
      console.log('[BlogPostSelector] Available content fields:', Object.keys(selectedBlog).filter(key => 
        ['content', 'body', 'text', 'excerpt', 'summary', 'metaDescription', 'description', 'intro', 'abstract'].includes(key)
      ));
    } else {
      // Fallback to form data if no selectedBlog
      blog = { 
        id: Date.now().toString(), 
        title, 
        excerpt, 
        content: excerpt, // Use excerpt as content if that's all we have
        url, 
        tags: tags.split(',').map(s => s.trim()).filter(Boolean) 
      };
      console.log('[BlogPostSelector] Using form data with content length:', blog.content?.length || 0);
    }
    
    setLoading(true);
    try {
      const opts = { tone, length, maxHashtags: 5 };
  let out = await clientGenerator.generateVariants(blog, templateId, opts);
  // enforce problem-focused titles if generator fell back or returned blog-title-like titles
  try { out = await clientGenerator.enforceProblemTitles(blog, out || []); } catch(e){}
  setVariants(out || []);
  if (onGenerated && out && out.length) onGenerated(out);
    } catch (err) {
      console.error(err);
      setError('Generation failed — check console for details.');
    } finally {
      setLoading(false);
    }
  }

  // Prefill when a blog was selected elsewhere in the app or passed as a prop
  useEffect(() => {
    try {
      const sel = selectedBlog || window.__BLOG_SELECTED_POST;
      if (sel) {
        console.log('[BlogPostSelector] Blog selected:', sel.title, 'with content length:', sel.content?.length || 0);
        setTitle(sel.title || '');
        setExcerpt(sel.excerpt || sel.summary || sel.body || '');
        setUrl(sel.url || '');
        setTags((sel.tags || []).join(','));
        // Log what content fields are available
        console.log('[BlogPostSelector] Available content fields in selected blog:', 
          Object.keys(sel).filter(key => 
            ['content', 'body', 'text', 'excerpt', 'summary', 'metaDescription', 'description', 'intro', 'abstract'].includes(key)
          )
        );
        // remove the global after consuming
        try { delete window.__BLOG_SELECTED_POST; } catch(e) { window.__BLOG_SELECTED_POST = undefined; }
      }
    } catch (e) {}
  }, [selectedBlog]);

  // Auto-generate once a blog is selected. Debounce a little to allow prop propagation.
  useEffect(() => {
    let t;
    try {
      const sel = selectedBlog;
      if (sel) {
        // small timeout so state updates flush before generation
        t = setTimeout(() => {
          // only auto-run if there are no existing variants (avoid clobber)
          if (variants.length === 0) handleGenerate();
        }, 300);
      }
    } catch(e){}
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBlog]);

  return (
    <div className="blog-post-selector">
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
        <span style={{ fontWeight: 600, fontSize: 16 }}>Blog → Creative</span>
        {llmStatus === 'ready' && <span style={{ background: '#22c55e', color: '#fff', borderRadius: 6, fontSize: 12, padding: '2px 8px', fontWeight: 500 }}>LLM powered</span>}
        {llmStatus !== 'ready' && <span style={{ background: '#fbbf24', color: '#23263a', borderRadius: 6, fontSize: 12, padding: '2px 8px', fontWeight: 500 }}>LLM not loaded</span>}
      </div>
      <div style={{ display: 'grid', gap: 10 }}>
        <input 
          placeholder="Blog title" 
          value={title} 
          onChange={e => setTitle(e.target.value)} 
          style={{ 
            background: '#2a2f3a', 
            border: '1px solid #374151', 
            borderRadius: '6px', 
            padding: '8px 12px', 
            color: '#fff', 
            fontSize: '14px' 
          }} 
        />
        <textarea 
          placeholder="Short excerpt or lead paragraph" 
          value={excerpt} 
          onChange={e => setExcerpt(e.target.value)} 
          rows={4} 
          style={{ 
            background: '#2a2f3a', 
            border: '1px solid #374151', 
            borderRadius: '6px', 
            padding: '8px 12px', 
            color: '#fff', 
            fontSize: '14px', 
            fontFamily: 'inherit', 
            resize: 'vertical' 
          }} 
        />
        <input 
          placeholder="Permalink (optional)" 
          value={url} 
          onChange={e => setUrl(e.target.value)} 
          style={{ 
            background: '#2a2f3a', 
            border: '1px solid #374151', 
            borderRadius: '6px', 
            padding: '8px 12px', 
            color: '#fff', 
            fontSize: '14px' 
          }} 
        />
        <input 
          placeholder="tags comma separated (e.g. dental, hygiene)" 
          value={tags} 
          onChange={e => setTags(e.target.value)} 
          style={{ 
            background: '#2a2f3a', 
            border: '1px solid #374151', 
            borderRadius: '6px', 
            padding: '8px 12px', 
            color: '#fff', 
            fontSize: '14px' 
          }} 
        />

        <label style={{ color: '#d1d5db', fontSize: '14px', fontWeight: '500' }}>
          Template
          <select 
            value={templateId} 
            onChange={e => setTemplateId(e.target.value)}
            style={{ 
              background: '#2a2f3a', 
              border: '1px solid #374151', 
              borderRadius: '6px', 
              padding: '8px 12px', 
              color: '#fff', 
              fontSize: '14px',
              width: '100%',
              marginTop: '4px'
            }}
          >
            {Object.entries(templates).map(([id, t]) => (
              <option key={id} value={id} style={{ background: '#2a2f3a', color: '#fff' }}>{t.displayName}</option>
            ))}
          </select>
        </label>

        <label style={{ color: '#d1d5db', fontSize: '14px', fontWeight: '500' }}>
          Tone
          <select 
            value={tone} 
            onChange={e => setTone(e.target.value)}
            style={{ 
              background: '#2a2f3a', 
              border: '1px solid #374151', 
              borderRadius: '6px', 
              padding: '8px 12px', 
              color: '#fff', 
              fontSize: '14px',
              width: '100%',
              marginTop: '4px'
            }}
          >
            <option value="professional" style={{ background: '#2a2f3a', color: '#fff' }}>Professional</option>
            <option value="casual" style={{ background: '#2a2f3a', color: '#fff' }}>Casual</option>
            <option value="inspirational" style={{ background: '#2a2f3a', color: '#fff' }}>Inspirational</option>
          </select>
        </label>

        <label style={{ color: '#d1d5db', fontSize: '14px', fontWeight: '500' }}>
          Length
          <select 
            value={length} 
            onChange={e => setLength(e.target.value)}
            style={{ 
              background: '#2a2f3a', 
              border: '1px solid #374151', 
              borderRadius: '6px', 
              padding: '8px 12px', 
              color: '#fff', 
              fontSize: '14px',
              width: '100%',
              marginTop: '4px'
            }}
          >
            <option value="short" style={{ background: '#2a2f3a', color: '#fff' }}>Short</option>
            <option value="medium" style={{ background: '#2a2f3a', color: '#fff' }}>Medium</option>
            <option value="long" style={{ background: '#2a2f3a', color: '#fff' }}>Long</option>
          </select>
        </label>

        <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
          <Button onClick={handleGenerate} disabled={loading} className={cn(loading ? 'opacity-70' : '')} style={{ fontWeight: 600, fontSize: 15, minWidth: 160, background: '#2563eb', color: '#fff' }}>{loading ? 'Generating…' : 'Generate variants'}</Button>
          <Button variant="ghost" onClick={() => { setTitle(''); setExcerpt(''); setUrl(''); setTags(''); setVariants([]); }}>Clear</Button>
        </div>

        {error && <div style={{ color: 'var(--danger, red)' }}>{error}</div>}
      </div>

  <hr style={{ margin: '12px 0', borderColor: '#23263a' }} />

      <div>
        <h4 style={{ color: '#d1d5db', fontSize: '16px', fontWeight: '600', marginBottom: '12px' }}>Variants</h4>
        {variants.length === 0 && <div style={{ color: '#9ca3af', fontSize: '14px', fontStyle: 'italic' }}>No variants yet. Generate to see them.</div>}
        <div style={{ display: 'grid', gap: 10 }}>
          {variants.map((v, i) => (
            <div key={i} style={{ 
              border: '1px solid #374151', 
              background: '#1f2937',
              padding: '12px', 
              borderRadius: '8px',
              transition: 'all 0.2s ease'
            }}>
              <strong style={{ color: '#f3f4f6', fontSize: '14px', fontWeight: '600', display: 'block', marginBottom: '6px' }}>{v.title}</strong>
              <p style={{ color: '#d1d5db', fontSize: '13px', margin: '0 0 8px 0', lineHeight: '1.4' }}>{v.message}</p>
              <p style={{ fontSize: '12px', color: '#9ca3af', margin: '0 0 10px 0' }}>{v.cta} • {v.hashtags?.join(' ')}</p>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button 
                  onClick={() => onApply && onApply(v)}
                  style={{
                    background: '#2563eb',
                    color: '#fff',
                    border: 'none',
                    padding: '6px 12px',
                    borderRadius: '5px',
                    fontSize: '12px',
                    fontWeight: '500',
                    cursor: 'pointer',
                    transition: 'background 0.2s ease'
                  }}
                  onMouseOver={(e) => e.target.style.background = '#1d4ed8'}
                  onMouseOut={(e) => e.target.style.background = '#2563eb'}
                >Apply to canvas</button>
                <button 
                  onClick={() => navigator.clipboard?.writeText(`${v.title}\n\n${v.message}\n\n${v.cta}\n\n${v.hashtags?.join(' ')}`)}
                  style={{
                    background: '#374151',
                    color: '#d1d5db',
                    border: 'none',
                    padding: '6px 12px',
                    borderRadius: '5px',
                    fontSize: '12px',
                    fontWeight: '500',
                    cursor: 'pointer',
                    transition: 'background 0.2s ease'
                  }}
                  onMouseOver={(e) => e.target.style.background = '#4b5563'}
                  onMouseOut={(e) => e.target.style.background = '#374151'}
                >Copy</button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
